import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Eye, Filter, Mail, Phone } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useToast } from "@/components/ui/use-toast";
import { businessTypeLabel } from "@/lib/quoteSchema";
import { preferredScheduleLabel } from "@/config/contact";
import { formatDate, formatRelativeDays } from "@/lib/utils";
import quoteService, {
  QUOTE_STATUSES,
  getLeadFreshness,
  type Quote,
  type QuoteStatus,
} from "@/services/quoteService";

/**
 * El logo lo elige el visitante: solo se abre o se embebe si es una URL http(s),
 * para no convertir el panel en un vector de phishing o de tracking del admin.
 */
const isSafeLogoUrl = (value?: string): boolean =>
  Boolean(value && /^https?:\/\//i.test(value));

/**
 * Panel de solicitudes de cotización B2B (HU-02).
 *
 * Lista las solicitudes capturadas en la home, permite ver el detalle
 * completo (incluido el logo) y avanzar el estado del lead. Solo accesible para
 * administradores — la ruta va envuelta en `AdminRoute`.
 */
const AdminQuotes = () => {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const [statusFilter, setStatusFilter] = useState<QuoteStatus | "all">("all");
  const [page, setPage] = useState(1);
  const [selected, setSelected] = useState<Quote | null>(null);
  const [nextStatus, setNextStatus] = useState<QuoteStatus>("nuevo");

  const { data, isLoading } = useQuery({
    queryKey: ["admin-quotes", statusFilter, page],
    queryFn: () =>
      quoteService.getQuotes({ status: statusFilter, page, limit: 10 }),
    staleTime: 30 * 1000,
  });

  const updateStatusMutation = useMutation({
    mutationFn: ({ id, status }: { id: string; status: QuoteStatus }) =>
      quoteService.updateQuoteStatus(id, status),
    onSuccess: (quote) => {
      queryClient.invalidateQueries({ queryKey: ["admin-quotes"] });
      setSelected(null);
      toast({
        title: "Estado actualizado",
        description: `La solicitud de ${quote.name} quedó en "${
          quoteService.getStatusLabel(quote.status)
        }".`,
      });
    },
    onError: () => {
      toast({
        variant: "destructive",
        title: "No se pudo actualizar el estado",
        description: "Intentá de nuevo en unos segundos.",
      });
    },
  });

  const quotes = data?.quotes ?? [];
  const pagination = data?.pagination;
  const selectedFreshness = selected ? getLeadFreshness(selected) : null;

  const { data: selectedPostSale } = useQuery({
    queryKey: ["admin-quote-postventa", selected?._id],
    queryFn: () => quoteService.getQuotePostSale(selected?._id ?? ""),
    enabled: Boolean(selected),
    retry: false,
  });

  const openDetails = (quote: Quote) => {
    setSelected(quote);
    setNextStatus(quote.status);
  };

  return (
    <div className="p-6 space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-playfair font-bold text-3xl text-brown-dark">
            Solicitudes de cotización
          </h1>
          <p className="text-brown-light">
            Pedidos corporativos capturados desde el sitio, con su estado de
            seguimiento.
          </p>
        </div>

        <div className="flex items-center gap-2">
          <Filter className="h-4 w-4 text-brown-light" />
          <Select
            value={statusFilter}
            onValueChange={(value) => {
              setStatusFilter(value as QuoteStatus | "all");
              setPage(1);
            }}
          >
            <SelectTrigger className="w-[190px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos los estados</SelectItem>
              {QUOTE_STATUSES.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total</CardTitle>
            <Mail className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-brown-dark">
              {pagination?.total ?? 0}
            </div>
            <p className="text-xs text-muted-foreground">Solicitudes</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Nuevos</CardTitle>
            <Eye className="h-4 w-4 text-primary-dark" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-brown-dark">
              {quotes.filter((q) => q.status === "nuevo").length}
            </div>
            <p className="text-xs text-muted-foreground">Sin contactar</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">En seguimiento</CardTitle>
            <Phone className="h-4 w-4 text-blue-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-brown-dark">
              {quotes.filter((q) => q.status === "contactado" || q.status === "cotizado").length}
            </div>
            <p className="text-xs text-muted-foreground">Contactados o cotizados</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Fríos</CardTitle>
            <Filter className="h-4 w-4 text-red-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-brown-dark">
              {quotes.filter((q) => getLeadFreshness(q).isCold).length}
            </div>
            <p className="text-xs text-muted-foreground">Sin avance ≥5 días</p>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-lg">
            {pagination ? `${pagination.total} solicitud(es)` : "Solicitudes"}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="space-y-3">
              {[0, 1, 2].map((row) => (
                <Skeleton key={row} className="h-12 w-full" />
              ))}
            </div>
          ) : quotes.length === 0 ? (
            <p className="text-brown-light py-8 text-center">
              Todavía no hay solicitudes de cotización con este filtro.
            </p>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Recibida</TableHead>
                    <TableHead>Último avance</TableHead>
                    <TableHead>Solicitante</TableHead>
                    <TableHead>Negocio</TableHead>
                    <TableHead>Cantidad</TableHead>
                    <TableHead>Estado</TableHead>
                    <TableHead className="text-right">Acciones</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {quotes.map((quote) => (
                    <TableRow key={quote._id}>
                      <TableCell className="whitespace-nowrap text-sm">
                        {formatDate(quote.createdAt)}
                      </TableCell>
                      <TableCell className="whitespace-nowrap text-sm">
                        <div>
                          {formatDate(quote.statusChangedAt ?? quote.createdAt)}
                        </div>
                        <div className="text-xs text-brown-light">
                          {formatRelativeDays(
                            quote.statusChangedAt ?? quote.createdAt
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="font-medium text-brown-dark">
                          {quote.name}
                        </div>
                        <div className="text-xs text-brown-light">
                          {quote.company || quote.email}
                        </div>
                      </TableCell>
                      <TableCell className="text-sm">
                        {quote.kind === "llamada"
                          ? `Llamada · ${preferredScheduleLabel(
                              quote.preferredSchedule
                            )}`
                          : businessTypeLabel(quote.businessType)}
                      </TableCell>
                      <TableCell className="text-sm">
                        {quote.kind === "llamada" ? "—" : quote.quantity}
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-wrap items-center gap-2">
                          <Badge
                            className={quoteService.getStatusColor(quote.status)}
                          >
                            {quoteService.getStatusLabel(quote.status)}
                          </Badge>
                          {getLeadFreshness(quote).isCold && (
                            <Badge className="bg-red-100 text-red-800">
                              Frío
                            </Badge>
                          )}
                          {quote.origin === "referido" && (
                            <Badge variant="secondary">Referido</Badge>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="text-right">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => openDetails(quote)}
                        >
                          <Eye className="h-4 w-4 mr-1" /> Ver
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}

          {pagination && pagination.pages > 1 && (
            <div className="flex items-center justify-between mt-4">
              <span className="text-sm text-brown-light">
                Página {pagination.page} de {pagination.pages}
              </span>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={pagination.page <= 1}
                  onClick={() => setPage((current) => Math.max(1, current - 1))}
                >
                  Anterior
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={pagination.page >= pagination.pages}
                  onClick={() => setPage((current) => current + 1)}
                >
                  Siguiente
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={Boolean(selected)} onOpenChange={() => setSelected(null)}>
        <DialogContent className="max-w-2xl">
          {selected && (
            <>
              <DialogHeader>
                <DialogTitle className="font-playfair">
                  {selected.name}
                  {selected.company ? ` · ${selected.company}` : ""}
                </DialogTitle>
                <DialogDescription>
                  Recibida el {formatDate(selected.createdAt)}
                </DialogDescription>
              </DialogHeader>

              <div className="space-y-4">
                <div className="flex flex-wrap items-center gap-2">
                  {selectedFreshness?.isCold && (
                    <Badge className="bg-red-100 text-red-800">Frío</Badge>
                  )}
                  {selected.origin === "referido" && (
                    <Badge variant="secondary">
                      Referido
                      {selected.referredByName
                        ? ` por ${selected.referredByName}`
                        : ""}
                    </Badge>
                  )}
                  {selected.nurture?.optOutAt && (
                    <Badge variant="secondary">Baja</Badge>
                  )}
                  {selected.nurture && (
                    <span className="text-xs text-brown-light">
                      Seguimiento {selected.nurture.stepsSent}/3
                    </span>
                  )}
                </div>

                {selected.status === "cerrado" && (
                  <div className="bg-background rounded-xl p-4 text-sm text-brown-light space-y-1">
                    <p className="font-medium text-brown-dark">Postventa</p>
                    <p>
                      Reconocimiento:{" "}
                      {selected.postventa?.recognitionSentAt
                        ? "enviado"
                        : "no enviado"}
                      {selected.postventa?.recognitionStatus === "failed"
                        ? " (el correo falló)"
                        : ""}
                    </p>
                    <p>
                      Testimonio:{" "}
                      {selectedPostSale?.testimonialSubmitted
                        ? "recibido"
                        : "sin testimonio"}
                    </p>
                    <p>Referidos: {selectedPostSale?.referralCount ?? 0}</p>
                  </div>
                )}

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
                  {selected.email && (
                    <div className="flex items-center gap-2">
                      <Mail className="h-4 w-4 text-brown-light" />
                      <a
                        className="text-primary-dark underline"
                        href={`mailto:${selected.email}`}
                      >
                        {selected.email}
                      </a>
                    </div>
                  )}
                  <div className="flex items-center gap-2">
                    <Phone className="h-4 w-4 text-brown-light" />
                    <a
                      className="text-primary-dark underline"
                      href={`tel:${selected.phone}`}
                    >
                      {selected.phone}
                    </a>
                  </div>
                  <div>
                    <span className="text-brown-light">Último avance: </span>
                    {formatDate(selected.statusChangedAt ?? selected.createdAt)}{" "}
                    ·{" "}
                    {formatRelativeDays(
                      selected.statusChangedAt ?? selected.createdAt
                    )}
                  </div>
                  {selected.kind === "llamada" ? (
                    <div>
                      <span className="text-brown-light">Tipo: </span>
                      Llamada ·{" "}
                      {preferredScheduleLabel(selected.preferredSchedule)}
                    </div>
                  ) : (
                    <>
                      <div>
                        <span className="text-brown-light">
                          Tipo de negocio:{" "}
                        </span>
                        {businessTypeLabel(selected.businessType)}
                      </div>
                      <div>
                        <span className="text-brown-light">
                          Cantidad estimada:{" "}
                        </span>
                        {selected.quantity}
                      </div>
                    </>
                  )}
                  <div>
                    <span className="text-brown-light">Producto: </span>
                    {selected.product || "A definir"}
                  </div>
                  <div>
                    <span className="text-brown-light">Fecha objetivo: </span>
                    {selected.targetDate
                      ? formatDate(selected.targetDate)
                      : "Sin definir"}
                  </div>
                </div>

                {selected.message && (
                  <div className="bg-background rounded-xl p-4 text-sm text-brown-light">
                    {selected.message}
                  </div>
                )}

                {isSafeLogoUrl(selected.logo) && (
                  <div>
                    <p className="text-sm text-brown-light mb-2">
                      Logo o referencia enviada
                    </p>
                    <a
                      href={selected.logo}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <img
                        src={selected.logo}
                        alt="Logo enviado con la solicitud"
                        className="max-h-40 rounded-xl border border-primary/15"
                        loading="lazy"
                      />
                    </a>
                  </div>
                )}

                {selected.notificationStatus === "failed" && (
                  <p className="text-sm text-amber-700 bg-amber-50 rounded-xl p-3">
                    El aviso por email no se pudo enviar
                    {selected.notificationError
                      ? ` (${selected.notificationError})`
                      : ""}
                    . La solicitud quedó guardada igual.
                  </p>
                )}

                <div className="border-t border-primary/15 pt-4">
                  <label className="block text-sm font-medium mb-2">
                    Estado de la solicitud
                  </label>
                  <div className="flex flex-col sm:flex-row gap-3">
                    <Select
                      value={nextStatus}
                      onValueChange={(value) =>
                        setNextStatus(value as QuoteStatus)
                      }
                    >
                      <SelectTrigger className="sm:w-[220px]">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {QUOTE_STATUSES.map((option) => (
                          <SelectItem key={option.value} value={option.value}>
                            {option.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Button
                      disabled={
                        updateStatusMutation.isPending ||
                        nextStatus === selected.status
                      }
                      onClick={() =>
                        updateStatusMutation.mutate({
                          id: selected._id,
                          status: nextStatus,
                        })
                      }
                    >
                      {updateStatusMutation.isPending
                        ? "Guardando…"
                        : "Guardar estado"}
                    </Button>
                  </div>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminQuotes;
